/**
 * MyCommissions — Rep-facing personal earnings page.
 *
 * Route: /commissions/mine (visible to all authenticated users; page shows
 *        "not on commission" hint if the user has no rate set).
 *
 * Sections:
 *   1. Header + rate badge ("You earn 5% on net profit · paid 1099")
 *   2. Four KPI tiles — This Period · Pending · Held (job not started) · Paid YTD
 *   3. Statement history — last 24 bi-weekly statements with status badges
 *   4. Accrual ledger — every accrual with deal/invoice/date/amount
 *
 * The rep can trace every dollar back to the invoice that generated it, which
 * is the single biggest driver of rep trust in the commission system.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { DollarSign, Clock, Lock, TrendingUp, FileText, Loader2, AlertTriangle, ExternalLink } from "lucide-react";

const STATUS_STYLE = {
  draft:  "bg-zinc-100 text-zinc-800",
  sent:   "bg-blue-100 text-blue-800",
  sent_to_rep: "bg-blue-100 text-blue-800",
  signed: "bg-emerald-100 text-emerald-800",
  paid:   "bg-emerald-600 text-white",
  void:   "bg-red-100 text-red-800",
};

const ACCRUAL_STATUS = {
  open:                "bg-blue-100 text-blue-800",
  pending_job_start:   "bg-amber-100 text-amber-900",
  paid:                "bg-emerald-100 text-emerald-800",
  reversed:            "bg-red-100 text-red-800",
};

const money = (v) => (Number(v || 0)).toLocaleString(undefined, { style: "currency", currency: "USD" });

function Tile({ label, value, hint, icon: Icon, tone = "zinc", testId }) {
  const toneMap = {
    zinc: "border-zinc-200 bg-white",
    emerald: "border-emerald-200 bg-emerald-50/40",
    amber: "border-amber-200 bg-amber-50/40",
    blue: "border-blue-200 bg-blue-50/40",
  };
  return (
    <div className={`rounded-sm border ${toneMap[tone]} p-4`} data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-600">{label}</div>
        <Icon className="w-4 h-4 text-zinc-400" />
      </div>
      <div className="font-heading text-2xl font-bold tracking-tight text-zinc-950">{value}</div>
      {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

export default function MyCommissions() {
  const [summary, setSummary] = useState(null);
  const [statements, setStatements] = useState([]);
  const [accruals, setAccruals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, st, ac] = await Promise.all([
          api.get("/commissions/mine/summary"),
          api.get("/commissions/mine/statements"),
          api.get("/commissions/mine/accruals"),
        ]);
        setSummary(s.data);
        setStatements(st.data || []);
        setAccruals(ac.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-zinc-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading commissions…
      </div>
    );
  }

  const noRate = summary && (summary.rate_pct === null || summary.rate_pct === undefined);

  return (
    <div className="p-6 sm:p-8 max-w-6xl" data-testid="my-commissions-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Sales</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">My Commissions</h1>
          <div className="text-sm text-zinc-600 mt-1">
            Period {summary?.period?.period_start} — {summary?.period?.period_end}
          </div>
        </div>
        {!noRate && (
          <div className="text-right text-xs" data-testid="my-commissions-rate-badge">
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Your Rate</div>
            <div className="text-2xl font-bold text-emerald-700 tabular-nums">{Number(summary.rate_pct).toFixed(2)}%</div>
            <div className="text-[11px] text-zinc-500">of net profit · {summary.payroll_type || "1099"}</div>
          </div>
        )}
      </div>

      {noRate && (
        <div className="bg-amber-50 border border-amber-300 rounded-sm px-4 py-3 mb-6 flex items-start gap-3" data-testid="my-commissions-no-rate">
          <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
          <div className="text-sm">
            <div className="font-semibold text-amber-900">No commission rate is set on your account yet.</div>
            <div className="text-amber-800">Ask an admin to set your rate under Users → your row → Commission Rate. Until then, no accruals will be posted to your ledger.</div>
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Tile
          label="This Period"
          value={money(summary?.this_period_total)}
          hint="Accrued during current bi-weekly window"
          icon={TrendingUp}
          tone="blue"
          testId="my-comm-tile-period"
        />
        <Tile
          label="Pending"
          value={money(summary?.pending_total)}
          hint="Accrued but not yet on a statement"
          icon={Clock}
          tone="amber"
          testId="my-comm-tile-pending"
        />
        <Tile
          label="Held — Job Not Started"
          value={money(summary?.held_pending_job_start)}
          hint="Deposit-invoice accruals awaiting Job Started ✓"
          icon={Lock}
          tone="zinc"
          testId="my-comm-tile-held"
        />
        <Tile
          label="Paid YTD"
          value={money(summary?.paid_ytd)}
          hint="Delivered via signed statements"
          icon={DollarSign}
          tone="emerald"
          testId="my-comm-tile-paid"
        />
      </div>

      {/* Statements */}
      <section className="mb-10">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-xl font-bold tracking-tight text-zinc-950">Statement History</h2>
          <span className="text-[11px] text-zinc-500">Last {statements.length} of 24 max</span>
        </div>
        {statements.length === 0 ? (
          <div className="border border-dashed border-zinc-300 rounded-sm p-8 text-center text-sm text-zinc-500" data-testid="my-comm-no-statements">
            No statements yet. They generate every other Friday.
          </div>
        ) : (
          <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="text-left px-3 py-2">Period</th>
                  <th className="text-left px-3 py-2">Generated</th>
                  <th className="text-right px-3 py-2">Amount</th>
                  <th className="text-center px-3 py-2">Status</th>
                  <th className="text-center px-3 py-2">Paid</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {statements.map((s) => (
                  <tr key={s.id} className="hover:bg-zinc-50" data-testid={`my-stmt-row-${s.id}`}>
                    <td className="px-3 py-2 font-medium">{s.period_start} → {s.period_end}</td>
                    <td className="px-3 py-2 text-zinc-600">{(s.generated_at || "").split("T")[0]}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold">{money(s.total)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${STATUS_STYLE[s.status] || "bg-zinc-100 text-zinc-800"}`}>
                        {s.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center text-xs text-zinc-600">
                      {s.paid_at ? s.paid_at.split("T")[0] : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Accrual ledger */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-xl font-bold tracking-tight text-zinc-950">Accrual Ledger</h2>
          <span className="text-[11px] text-zinc-500">Every invoice payment that credited you</span>
        </div>
        {accruals.length === 0 ? (
          <div className="border border-dashed border-zinc-300 rounded-sm p-8 text-center text-sm text-zinc-500" data-testid="my-comm-no-accruals">
            No accruals yet. Each time a customer pays an invoice on a deal assigned to you, a row will appear here showing exactly how much you earned.
          </div>
        ) : (
          <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Deal</th>
                  <th className="text-left px-3 py-2">Invoice</th>
                  <th className="text-right px-3 py-2">Payment</th>
                  <th className="text-right px-3 py-2">Rate</th>
                  <th className="text-right px-3 py-2">You Earned</th>
                  <th className="text-center px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {accruals.map((a) => (
                  <tr key={a.id} className="hover:bg-zinc-50" data-testid={`my-accrual-row-${a.id}`}>
                    <td className="px-3 py-2 whitespace-nowrap text-zinc-700">{(a.accrued_at || "").split("T")[0]}</td>
                    <td className="px-3 py-2">
                      {a.deal_id ? (
                        <Link to={`/deals/${a.deal_id}`} className="text-blue-700 hover:underline inline-flex items-center gap-1">
                          {a.deal_title || a.deal_id.slice(0, 8)}
                          <ExternalLink className="w-3 h-3" />
                        </Link>
                      ) : "—"}
                    </td>
                    <td className="px-3 py-2 text-zinc-700">{a.invoice_number || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{money(a.payment_amount_collected)}</td>
                    <td className="px-3 py-2 text-right text-zinc-600">{Number(a.rate_pct_at_time || 0).toFixed(2)}%</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums font-bold text-emerald-700">{money(a.commission_amount)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${ACCRUAL_STATUS[a.status] || "bg-zinc-100 text-zinc-800"}`}>
                        {(a.status || "").replace("_", " ")}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
